/** Primitives and enums shared by every other schema module. */
import { z } from 'zod';

export const slug =z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}$/, 'lowercase letters, digits and hyphens, 2–63 chars');
export const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/, 'uuid v7');

export const OrgRole = z.enum(['owner', 'admin', 'member', 'guest', 'viewer']);
export type OrgRole = z.infer<typeof OrgRole>;
export const ProjectRole = z.enum(['maintainer', 'release_manager', 'developer', 'client', 'viewer']);
export type ProjectRole = z.infer<typeof ProjectRole>;
export const AppFramework = z.enum(['expo', 'react_native', 'ios_native', 'android_native', 'flutter']);
export type AppFramework = z.infer<typeof AppFramework>;
export const EnvKind = z.enum(['development', 'preview', 'staging', 'preprod', 'production']);
export type EnvKind = z.infer<typeof EnvKind>;

/** Scopes (api/openapi.yaml `Scope`). The API derives them from the membership; tokens carry a subset. */
export const Scope = z.enum([
  'admin', 'projects:read', 'projects:write', 'builds:read', 'builds:write', 'env:read', 'env:write', 'env:reveal',
  'submissions:read', 'submissions:write', 'releases:read', 'releases:write', 'ota:read', 'ota:write',
  'approvals:read', 'approvals:decide', 'approvals:write', 'credentials:read', 'credentials:write', 'webhooks:read', 'webhooks:write', 'audit:read',
]);
export type Scope = z.infer<typeof Scope>;

export const SCOPES_BY_ORG_ROLE: Record<OrgRole, Scope[]> = {
  owner: Scope.options,
  admin: Scope.options,
  member: ['projects:read', 'projects:write', 'builds:read', 'builds:write', 'env:read', 'env:write', 'env:reveal', 'submissions:read', 'submissions:write', 'releases:read', 'releases:write', 'ota:read', 'ota:write', 'approvals:read', 'credentials:read', 'webhooks:read', 'audit:read'],
  guest: ['projects:read', 'builds:read', 'submissions:read', 'releases:read', 'ota:read'],
  viewer: ['projects:read', 'builds:read', 'env:read', 'submissions:read', 'releases:read', 'ota:read', 'approvals:read', 'audit:read'],
};

export const Platform = z.enum(['ios', 'android']);
export type Platform = z.infer<typeof Platform>;
export const EnvVisibility = z.enum(['plain', 'sensitive', 'secret']);
export type EnvVisibility = z.infer<typeof EnvVisibility>;
export const EnvValueType = z.enum(['string', 'multiline', 'file', 'json']);
export type EnvValueType = z.infer<typeof EnvValueType>;
export const EnvScope = z.enum(['org', 'project', 'app']);
export const ExecutionMode = z.enum(['provider_api', 'ci_client', 'hangar_runner']);
export type ExecutionMode = z.infer<typeof ExecutionMode>;
export const ProviderKind = z.enum(['eas', 'bitrise']);
export type ProviderKind = z.infer<typeof ProviderKind>;
export const ArtifactKind = z.enum(['aab', 'apk', 'ipa', 'app_zip', 'app_tar_gz', 'dsym', 'mapping', 'sourcemap', 'log', 'metadata', 'sbom']);
export type ArtifactKind = z.infer<typeof ArtifactKind>;
export const BuildStatus = z.enum(['pending_approval', 'awaiting_client', 'queued', 'dispatching', 'provider_queued', 'running', 'verifying', 'succeeded', 'failed', 'errored', 'canceled', 'timed_out', 'expired']);
export type BuildStatus = z.infer<typeof BuildStatus>;
export const BuildPriority = z.enum(['low', 'normal', 'high', 'urgent']);
export type BuildPriority = z.infer<typeof BuildPriority>;
export const PRIORITY_VALUE: Record<BuildPriority, number> = { urgent: 10, high: 50, normal: 100, low: 200 };
export const CheckStatus = z.enum(['passed', 'failed', 'warning', 'skipped', 'error']);
export const TriggerSource = z.enum(['ui', 'sdk', 'api', 'scm_webhook', 'schedule', 'redeploy', 'promotion']);
export type TriggerSource = z.infer<typeof TriggerSource>;

/**
 * Highest build number (Android versionCode, iOS CFBundleVersion as Hangar allocates it) any input may carry: Google
 * Play's versionCode limit. A shadow run's observed number can raise the app's floor, which never goes down.
 */
export const BUILD_NUMBER_MAX = 2_100_000_000;

/** Label of a `builds.priority` value (`PRIORITY_VALUE` inverted; unknown values map to the nearest label). */
export function priorityLabel(value: number): BuildPriority {
  let best: BuildPriority = 'normal';
  let distance = Infinity;
  for (const [label, v] of Object.entries(PRIORITY_VALUE) as [BuildPriority, number][]) {
    const d = Math.abs(v - value);
    if (d < distance) { distance = d; best = label; }
  }
  return best;
}
